<template>
  <BaseCaseCard
    :case-name="caseData.name"
    :case-image="caseImage"
    :item-image="caseData.itemImage"
    :parallax-config="caseParallaxConfig"
    wrapper-class="singleCase-wrapper--image-container"
  >
    <template #top-left-badge>
      <div v-if="wpEarnings > 0" class="wp-earn">+{{ formattedWpEarnings }} <WpIcon /></div>
      <div class="wp-earn" v-else-if="caseData.isEventFeatured && showWpIconOnMainPage">
        <WpIcon />
      </div>
    </template>

    <template #top-right-badge>
      <div class="price-box" :class="{ 'has-discount': hasDiscount }">
        <template v-if="hasDiscount">
          <!-- Discount badge removed -->
          <div class="price-stack">
            <Price :value="caseData.fullPrice" class="full-price" />
            <Price :value="caseData.price" class="actual-price" />
          </div>
        </template>
        <template v-else>
          <Price v-if="caseData.price > 0" :value="caseData.price" />
          <div v-else class="token-price">
            {{ caseData.tokenPrice }}
            <i class="fa-solid fa-ticket"></i>
          </div>
        </template>
      </div>
    </template>

    <template #bottom-left-badge>
      <div v-if="hasDiscount" class="discount-ribbon-wrapper">
        <div class="discount-ribbon">
          -{{ caseData.discountPercent }}%
        </div>
      </div>
    </template>
  </BaseCaseCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseCaseCard from '@/components/BaseCaseCard.vue'
import Price from '@/components/Price/Price.vue'
import WpIcon from '@/components/WpIcon.vue'
import { Case } from '@/services/models/models'
import type { ParallaxConfig } from '@/composables/useCardParallax'
import { formatAbbreviatedNumber } from '@/utils/formatNumber'

interface Props {
  caseData: Case
  wpEarnings?: number
  showWpIconOnMainPage?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  wpEarnings: 0,
})

const formattedWpEarnings = computed(() => formatAbbreviatedNumber(props.wpEarnings))

const hasDiscount = computed(() => (props.caseData.discountPercent ?? 0) > 0)

const caseImage = computed(() => props.caseData.image.replace('/media/cases/', '/media/cases-new/'))

const caseParallaxConfig: ParallaxConfig = {
  card: {
    rotateYFactor: -5,
    rotateXFactor: 7,
    translateY: -3,
  },
  background: {
    parallaxXFactor: -1.4,
    parallaxYFactor: -1.8,
    scale: 1.004,
  },
  case: {
    parallaxXFactor: -2.2,
    parallaxYFactor: -2.8,
    scale: 1.003,
  },
  weapon: {
    parallaxXFactor: -2.8,
    parallaxYFactor: -3.4,
    rotateFactor: -0.35,
    scale: 1.006,
  },
}
</script>

<style lang="scss" scoped>
.price-box {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 32px;
  padding: 0.45rem 0.7rem;
  background-color: rgba(10, 12, 18, 0.92);
  border-radius: 6px;
  font-size: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  z-index: 5;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @include mobile {
    font-size: 0.85rem;
    min-height: 28px;
    padding: 0.35rem 0.55rem;
  }

  &.has-discount {
    padding: 0.35rem 0.55rem;
    gap: 4px;
    align-items: center;
  }

  .price-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    line-height: 1.1;

    .full-price {
      font-size: 0.7em;
      color: rgba(255, 255, 255, 0.5);
      text-decoration: line-through;
      font-weight: 500;
      
      :deep(.price) {
        color: rgba(255, 255, 255, 0.5);
        background: none;
        -webkit-text-fill-color: rgba(255, 255, 255, 0.5);
        
        &::before {
             background: none;
            -webkit-text-fill-color: rgba(255, 255, 255, 0.5);
            color: rgba(255, 255, 255, 0.5);
        }
      }
    }

    .actual-price {
      font-size: 1.0em;
      filter: drop-shadow(0 0 8px rgba(217, 229, 94, 0.3));
    }
  }

  .token-price {
    font-weight: 700;
    color: #fff;
  }
}

.wp-earn {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  padding: 0.4rem 0.7rem;
  background: rgba(26, 26, 26, 0.9);
  color: #c8f24f;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  z-index: 5;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  @include mobile {
    font-size: 0.7rem;
    padding: 0.4rem 0.5rem;
  }
}

:deep(.singleCase-wrapper:hover) {
  .price-box {
    background-color: rgba(10, 12, 18, 0.92);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: none;
  }

  .wp-earn {
    background-color: rgba(26, 26, 26, 0.95);
    border-color: rgba(200, 242, 79, 0.3);
    box-shadow: 0 4px 10px rgba(200, 242, 79, 0.3);
  }
}

.discount-ribbon-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 80px;
  overflow: hidden;
  border-bottom-left-radius: 12px;
  z-index: 6;
  pointer-events: none;

  .discount-ribbon {
    position: absolute;
    bottom: 18px;
    left: -22px;
    width: 100px;
    background: linear-gradient(135deg, #e44f4f 0%, #d63a3a 100%);
    color: white;
    text-align: center;
    font-weight: 800;
    font-size: 0.85rem;
    padding: 4px 0;
    transform: rotate(45deg);
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.2);
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);

    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%);
        pointer-events: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 50%;
      height: 100%;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.4) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transform: skewX(-25deg);
      animation: ribbon-shine 3s infinite;
    }
  }
}

@keyframes ribbon-shine {
  0% {
    left: -100%;
    opacity: 0;
  }
  20% {
    left: 200%;
    opacity: 1;
  }
  100% {
    left: 200%;
    opacity: 0;
  }
}
</style>
